import { useMemo } from "react";
import { useSelector } from "react-redux";
import useEffectiveTeacherIdentity from "./useEffectiveTeacherIdentity";
import {
  gradeSortValue,
  parseDateValue,
  selectPreferredGrade,
} from "../util/teacherImplementations";
import { ISAFEVENTURES_ENDPOINT } from "../util/constant";

const DEFAULT_GRADE_CODES = ["K","1","2","3","4","5","6","7","8","9","10","11","12"];

const buildCurriculumUrl = (userId, sessionId) => {
  if (!userId || !sessionId || !ISAFEVENTURES_ENDPOINT) return null;
  const params = new URLSearchParams({
    var: String(userId),
    log: String(sessionId),
    destination: "direct/curriculum/search",
  });
  return `${ISAFEVENTURES_ENDPOINT}/direct/curriculum/search?${params.toString()}`;
};

export default function useCurriculumLink() {
  const { teacherId } = useEffectiveTeacherIdentity();
  const authUser = useSelector((state) => state?.auth?.user);
  const rolesRaw = useSelector((state) => state?.auth?.user?.roles);
  const teacherImplementationsRaw = useSelector(
    (state) => state?.auth?.teacher_implementations
  );
  const selectedSubscriptionRaw = useSelector(
    (state) => state?.auth?.selected_subscriptions
  );

  const roles = useMemo(
    () => (Array.isArray(rolesRaw) ? rolesRaw : []),
    [rolesRaw]
  );
  const teacherImplementations = useMemo(
    () => (Array.isArray(teacherImplementationsRaw) ? teacherImplementationsRaw : []),
    [teacherImplementationsRaw]
  );
  const selectedSubscription =
    selectedSubscriptionRaw && typeof selectedSubscriptionRaw === "object"
      ? selectedSubscriptionRaw
      : {};
  const isSubscriptionAdmin = roles.some(
    (role) => role?.id === "subscription_admin"
  );
  const userSessionId = authUser?.session_id ?? null;
  const authUserId = authUser?.user_id ?? null;

  return useMemo(() => {
    const hasImplementations =
      Array.isArray(teacherImplementations) && teacherImplementations.length > 0;

    const buildAdminFallback = () => {
      if (!isSubscriptionAdmin) return null;
      const derivedUserId = teacherId ?? authUserId ?? null;
      // Prefer fresh session_id from teacher-implementations_new over stale
      // auth.user.session_id so curriculum SSO (log=) stays in sync.
      let apiSessionId = null;
      if (Array.isArray(teacherImplementations)) {
        for (const impl of teacherImplementations) {
          if (impl?.session_id) {
            apiSessionId = impl.session_id;
            break;
          }
          const grades = Array.isArray(impl?.grade_codes)
            ? impl.grade_codes
            : [];
          const gradeWithSession = grades.find((g) => g?.session_id);
          if (gradeWithSession?.session_id) {
            apiSessionId = gradeWithSession.session_id;
            break;
          }
        }
      }
      const effectiveSessionId = apiSessionId ?? userSessionId ?? null;
      if (!derivedUserId || !effectiveSessionId) return null;
      const derivedSchoolId =
        (Array.isArray(teacherImplementations)
          ? teacherImplementations.find((impl) => impl.school_id)?.school_id
          : null) ??
        selectedSubscription?.school_id ??
        null;
      const fallbackGrades = DEFAULT_GRADE_CODES.map((code) => ({
        grade_code: code,
        user_id: derivedUserId,
        session_id: effectiveSessionId,
        school_id: derivedSchoolId,
        status: "0",
        status1: "0",
      }));
      return {
        user_id: derivedUserId,
        session_id: effectiveSessionId,
        school_id: derivedSchoolId,
        grade_codes: fallbackGrades,
      };
    };

    let downloadImplementation;
    if (!hasImplementations) {
      downloadImplementation = buildAdminFallback();
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const gradeMap = new Map();

      teacherImplementations.forEach((impl) => {
        const implEndDate = parseDateValue(impl?.end_date);
        const implementationActive =
          !implEndDate || implEndDate.getTime() >= today.getTime();
        const gradeCodes = Array.isArray(impl?.grade_codes)
          ? impl.grade_codes
          : [];

        gradeCodes.forEach((grade) => {
          const code = String(grade?.grade_code ?? "").trim();
          if (!code) return;

          const gradeEndDate = parseDateValue(grade?.end_date ?? impl?.end_date);
          if (gradeEndDate && gradeEndDate.getTime() < today.getTime()) {
            return;
          }
          if (!implementationActive && !gradeEndDate) {
            return;
          }

          const candidate = {
            ...grade,
            grade_code: code,
            session_id: grade.session_id ?? impl.session_id ?? null,
            user_id: grade.user_id ?? impl.user_id ?? null,
            school_id: grade.school_id ?? impl.school_id ?? null,
            implementation_id: impl.implementation_id,
            implementation_end_date: impl.end_date ?? null,
          };

          const existing = gradeMap.get(code);
          gradeMap.set(code, selectPreferredGrade(existing, candidate));
        });
      });

      const gradeList = Array.from(gradeMap.values()).sort((a, b) => {
        const aKey = gradeSortValue(a.grade_code);
        const bKey = gradeSortValue(b.grade_code);
        if (aKey !== bKey) return aKey - bKey;
        return String(a.grade_code ?? "").localeCompare(
          String(b.grade_code ?? "")
        );
      });

      if (gradeList.length === 0) {
        downloadImplementation = buildAdminFallback();
      } else {
        const fallback =
          gradeList.find((g) => g.user_id && g.session_id) ?? gradeList[0];
        const fallbackUserId =
          fallback.user_id ??
          teacherImplementations.find((impl) => impl.user_id)?.user_id ??
          teacherId ??
          authUserId ??
          null;
        const fallbackSessionId =
          fallback.session_id ??
          teacherImplementations.find((impl) => impl.session_id)?.session_id ??
          (isSubscriptionAdmin ? userSessionId : null) ??
          null;
        const fallbackSchoolId =
          fallback.school_id ??
          teacherImplementations.find((impl) => impl.school_id)?.school_id ??
          selectedSubscription?.school_id ??
          null;
        downloadImplementation = {
          user_id: fallbackUserId,
          session_id: fallbackSessionId,
          school_id: fallbackSchoolId,
          grade_codes: gradeList,
        };
      }
    }

    if (!downloadImplementation) return null;
    return buildCurriculumUrl(
      downloadImplementation.user_id,
      downloadImplementation.session_id
    );
  }, [
    authUserId,
    isSubscriptionAdmin,
    selectedSubscription?.school_id,
    teacherId,
    teacherImplementations,
    userSessionId,
  ]);
}
